import { useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import { TerminalContext } from "context/TerminalContext";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./styles.module.css";
import RePrinterIcon from "images/svg/printer-grey.svg";
import PayIcon from "images/svg/pay.svg";
import EditIcon from "images/svg/edit.svg";
import DeleteIcon from "images/svg/trash.svg";
import ChangeTableIcon from "images/svg/changeTable.svg";
import SplitTableIcon from "images/svg/splitTable.svg";
import MergeTableIcon from "images/svg/mergeTable.svg";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import { useNavigate } from "react-router-dom";
import SalesContext from "context/SalesContext";
import {
  KOT_TITLES,
  ROUTES,
  SALE_STATUS,
  KOT_STATUS,
  KOT_STATUS_COLORS,
  KEYBOARD_KEYS,
  MERGE_LABELS,
  SYSTEM_VIEW,
  EVENTS,
  STAFF_PERMISSION,
} from "const";
import { eventsEmitter } from "utils/events";
import CartDeleteConfirmPopup from "../CartDeleteConfirmPopup/CartDeleteConfirmPopup";
import SaleEditDialog from "../SaleEditDialog/SaleEditDialog";
import ShopContext from "context/ShopContext";
import SplitOrderDialog from "../SplitOrderDialog/SplitOrderDialog";
import { usePermission } from "hooks/usePermission";
import toast from "react-hot-toast";
import { amountRoundOff, quantityRoundOff } from "helpers/NumberFormatHelper";
import KotManager from "helpers/KotManager";
import ProductContext from "context/ProductContext";
import KOTInvoice from "components/utils/PrintInvoice/KOTInvoice";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import * as TerminalHelper from "helpers/TermialHelper";
import ShopsHelper from "helpers/ShopsHelper";

export default function CartDetailsDialog({
  open,
  onClose,
  saleId,
  onTableSelectionMode,
  onTableMergeMode,
  mergeLabel = MERGE_LABELS.TABLE,
}) {
  const navigate = useNavigate();
  const { setActiveSale, handleDeleteSale, handleSplitOrder, createNewSoftSale, sales, updateSaleKotStatus } =
    useContext(SalesContext) || {};
  const { selectedShop } = useContext(ShopContext);

  const { categories } = useContext(ProductContext);
  const { tableDashboard } = useContext(TerminalContext) || {};

  const [confirmOpen, setConfirmOpen] = useState(false);
  const { checkPermissionAndExecute, PermissionDialog } = usePermission();
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [printData, setPrintData] = useState(null);

  const showOrderStatus = useMemo(() => {
    return ShopsHelper.showOrderStatus(selectedShop);
  }, [selectedShop]);

  const sale = sales.find((s) => s.local_id === saleId);

  const kotPrintRef = useRef(null);

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: kotPrintRef,
    documentTitle: `KOT-${sale?.bill_no || "NA"}`,
  });

  const handleRePrintKOT = useCallback(() => {
    try {
      KotManager.printKot({
        sale: sale,
        categories,
        fallbackPrintFunction: handleWebInvoicePrint,
        titles: [KOT_TITLES.REPRINT],
      });

      setPrintData(sale);
    } catch (err) {
      toast.error("Error printing KOT");
    }
  }, [sale, categories, handleWebInvoicePrint]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open || !sale || billDialogOpen || splitDialogOpen || confirmOpen) return;

      if (event.key === KEYBOARD_KEYS.F3) {
        event.preventDefault();
        handleRePrintKOT();
      } else if (event.key === KEYBOARD_KEYS.F4) {
        event.preventDefault();
        setBillDialogOpen(true);
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [open, sale, billDialogOpen, splitDialogOpen, confirmOpen, handleRePrintKOT]);

  const saleItems = sale?.items ?? [];

  if (!open || !sale) return null;

  const handleEditClick = () => {
    if (typeof setActiveSale === "function") {
      setActiveSale(JSON.parse(JSON.stringify(sale)));
    }

    onClose?.();
    const settings = TerminalHelper.getTerminalSettings() || {};
    if (settings.systemType === SYSTEM_VIEW.ERP) {
      navigate(ROUTES.ERP_DASHBOARD);
    } else {
      navigate(ROUTES.SALES);
    }
  };

  const handleDeleteClick = () => {
    checkPermissionAndExecute(
      STAFF_PERMISSION.DELETE_CART,
      () => {
        setConfirmOpen(true);
      },
      "Delete Access",
    );
  };

  const handleConfirmDelete = (description) => {
    handleDeleteSale?.(sale?.local_id, description);
    setConfirmOpen(false);
    onClose?.();
  };

  const handleSplitTable = () => {
    const totalQty = saleItems.reduce((acc, item) => acc + Number(item.qty || 1), 0);
    if (totalQty <= 1) {
      toast.error("Item quantity must be greater than 1 to split order");
      setSplitDialogOpen(false);
      return;
    }

    setSplitDialogOpen(true);
  };

  const handleNewOrder = () => {
    try {
      createNewSoftSale({ table: sale.table });
      onClose?.();
      navigate(ROUTES.SALES);
    } catch (err) {
      toast.error("Error creating new order");
    }
  };

  return (
    <>
      {!billDialogOpen && (
        <div className={styles.popUp} onClick={onClose}>
          <Grid container className={styles.billContentWrapper} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <Grid item xs={12} className={styles.headSec} mb={2}>
              <div className={styles.headerContent}>
                <h3>Cart Details</h3>
                {sale?.original_sale_bill_no && (
                  <div className={styles.editingSale}>Editing Sale - Bill No: {sale?.original_sale_bill_no}</div>
                )}
              </div>
              <div className={styles.headerActions}>
                <ButtonRed onClick={handleNewOrder}>Start New Order</ButtonRed>
                <CloseIcon className={styles.icon} onClick={onClose} />
              </div>
            </Grid>

            {/* Meta info section */}
            <Grid item xs={12} className={styles.headInfoSec}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Table</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      {sale?.table?.name
                        ? `${sale.table.name}${sale?.table?.chair?.name ? ` (${sale.table.chair.name})` : ""}`
                        : "-"}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Date & Time</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{DateTimeHelpers.formatDateTime(sale.time)}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Cart Number</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{sale.local_id}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Order Type</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{sale?.order_type ?? "-"}</div>
                  </div>
                </Grid>

                <Grid item xs={4}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Pre Paid</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(sale.prepaid ?? 0)}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Round-off</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(sale.roundOff)}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Payable</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(sale.payable)}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Due Amount</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountRed}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(
                        sale?.status === SALE_STATUS.PAID && sale?.due === 0
                          ? 0
                          : Number(sale?.due ?? Number(sale.payable) - (sale.prepaid ?? 0)),
                      )}
                    </div>
                  </div>
                </Grid>

                <Grid item xs={4}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Items</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{saleItems.length}</div>
                  </div>
                  {sale.customer && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Customer</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>{sale.customer?.name}</div>
                    </div>
                  )}
                  {sale?.token_no && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Token Number</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>{sale.token_no}</div>
                    </div>
                  )}
                  <div className={styles.statusText}>
                    <div className={styles.subTitle}>Status</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountRed}>{sale.status}</div>
                  </div>

                  {(sale?.bill_no || sale?.original_sale_bill_no) && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Bill Number</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>{sale.bill_no || sale.original_sale_bill_no}</div>
                    </div>
                  )}
                  {sale?.waiter && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Waiter</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>{sale.waiter?.name}</div>
                    </div>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Order Status Transitions */}
            {showOrderStatus && (
              <Grid item xs={12} className={styles.orderStatusSec}>
                <div className={styles.orderStatusLabel}>Order Status</div>
                <div className={styles.statusButtons}>
                  {[KOT_STATUS.PENDING, KOT_STATUS.PREPARING, KOT_STATUS.READY, KOT_STATUS.DISPATCH].map((status) => {
                    const isActive = (sale.kot_status || KOT_STATUS.PENDING) === status;
                    const label = status;
                    return (
                      <div
                        key={status}
                        className={`${styles.statusBtn} ${isActive ? styles.statusBtnActive : ""}`}
                        style={{
                          background: isActive ? KOT_STATUS_COLORS[status] : "rgba(240, 240, 242, 1)",
                          color: isActive ? "#fff" : "rgba(124, 124, 125, 1)",
                        }}
                        onClick={() => updateSaleKotStatus(sale, status)}
                      >
                        {label}
                      </div>
                    );
                  })}
                </div>
              </Grid>
            )}

            {/* Items Table */}
            <Grid item xs={12} className={styles.tableContainer}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ width: "10%" }}>No</th>
                    <th style={{ width: "45%" }}>Items</th>
                    <th style={{ width: "15%", textAlign: "right" }}>Rate</th>
                    <th style={{ width: "15%", textAlign: "center" }}>Qty</th>
                    <th style={{ width: "15%", textAlign: "right" }}>Total</th>
                  </tr>
                </thead>

                <tbody>
                  {saleItems.map((it, i) => {
                    const qty = it.count ?? it.qty ?? 1;
                    const rate = it.rateAndTax ?? 0;
                    const itemTotal = qty * rate;

                    return (
                      <tr key={it.id ?? it._id ?? i}>
                        <td style={{ width: "10%" }}>{i + 1}</td>
                        <td style={{ width: "45%" }}>
                          <div>
                            {it.name} {it.option?.label ? ` - ${it.option.label}` : ""}
                          </div>
                          {it.note ? (
                            <div style={{ fontSize: "11px", color: "#666", marginTop: "2px", fontStyle: "italic" }}>
                              Note: {it.note}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ width: "15%", textAlign: "right" }}>{amountRoundOff(rate)}</td>
                        <td style={{ width: "15%", textAlign: "center" }}>{qty}</td>
                        <td style={{ width: "15%", textAlign: "right" }}>{amountRoundOff(itemTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className={styles.tfootRow}>
                  <tr className={styles.tfootTr}>
                    <td colSpan="2" className={styles.tfootLabel}>
                      Total
                    </td>

                    <td className={styles.tfootQty}>
                      {quantityRoundOff(saleItems.reduce((acc, it) => acc + Number(it.qty ?? 1), 0))}
                    </td>
                    <td className={styles.tfootTotal}>{amountRoundOff(sale.payable)}</td>
                  </tr>
                </tfoot>
              </table>
            </Grid>

            <div className={styles.border}></div>

            {/* Footer */}
            {sale?.status !== SALE_STATUS.PAID && (
              <Grid item xs={12} className={styles.footSec} mt={2}>
                {sale?.status !== SALE_STATUS.PRINTED && (
                  <>
                    <ButtonGray
                      className={styles.buttonGray}
                      startIcon={<img src={EditIcon} alt="Print" className={styles.iconSmall} />}
                      onClick={handleEditClick}
                    >
                      Edit
                    </ButtonGray>
                    {!sale?.editing_sale_local_id && (
                      <ButtonGray
                        color="error"
                        className={styles.buttonGray}
                        startIcon={<img src={DeleteIcon} alt="Print" className={styles.iconSmall} />}
                        onClick={handleDeleteClick}
                      >
                        Delete
                      </ButtonGray>
                    )}

                    <div className={styles.separator}></div>

                    {tableDashboard && (
                      <ButtonGray
                        className={styles.buttonGray}
                        startIcon={<img src={ChangeTableIcon} alt="Table" className={styles.iconSmall} />}
                        onClick={() => {
                          onTableSelectionMode?.(sale);
                          onClose?.();
                        }}
                      >
                        Change Table
                      </ButtonGray>
                    )}

                    {!sale?.editing_sale_local_id && (
                      <ButtonGray
                        className={styles.buttonGray}
                        onClick={handleSplitTable}
                        startIcon={<img src={SplitTableIcon} alt="Split" className={styles.iconSmall} />}
                      >
                        Split
                      </ButtonGray>
                    )}

                    {!sale?.editing_sale_local_id && (
                      <ButtonGray
                        className={styles.buttonGray}
                        onClick={() => {
                          onTableMergeMode?.(sale);
                          onClose?.();
                        }}
                        startIcon={<img src={MergeTableIcon} alt="Merge" className={styles.iconSmall} />}
                      >
                        {mergeLabel}
                      </ButtonGray>
                    )}
                    <div className={styles.separator}></div>
                  </>
                )}

                <ButtonGray
                  className={styles.buttonGray}
                  startIcon={<img src={RePrinterIcon} alt="Print" className={styles.iconSmall} />}
                  onClick={handleRePrintKOT}
                >
                  Re-Print KOT
                </ButtonGray>

                <ButtonRed
                  className={styles.footerBtn}
                  startIcon={<img src={PayIcon} alt="Print" className={styles.iconSmall} />}
                  onClick={() => {
                    setActiveSale(sale);
                    setBillDialogOpen(true);
                  }}
                >
                  Pay
                </ButtonRed>
              </Grid>
            )}
          </Grid>
        </div>
      )}

      <CartDeleteConfirmPopup
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {billDialogOpen && (
        <SaleEditDialog
          handleClose={() => setBillDialogOpen(false)}
          onDone={() => {
            setBillDialogOpen(false);
            onClose?.();
          }}
          saleLocalId={saleId}
        />
      )}

      <SplitOrderDialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        sale={sale}
        onSplit={(items) => {
          handleSplitOrder(sale, items);
          setSplitDialogOpen(false);
          onClose?.();
        }}
      />
      <div style={{ display: "none" }}>
        <KOTInvoice ref={kotPrintRef} sale={printData} />
      </div>

      <PermissionDialog />
    </>
  );
}
